import type { AppState } from '@auth0/auth0-vue';
import { StorageHelper } from '@smg-real-estate/hg-search-libs';

import { mergeGuestDrafts, type MergeGuestDraftsResponse } from '@/api/guests';
import { parseJwt } from '@/helpers/authUtils';
import { isIs24LegacyGuestWhitelisted } from '@/helpers/is24LegacyGuestWhitelisted';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { useNavigationStore } from '@/stores/navigation';
import {
  FeatureFlagName,
  PaymentType,
  UserRedirectedNextStepTypes,
  type AuthorizedUser,
} from '@/types';

export const setUserRedirectedNextStep = (appStateData: AppState | undefined) => {
  if (appStateData?.type === UserRedirectedNextStepTypes.PAYMENT && appStateData?.paymentType) {
    const navigationStore = useNavigationStore();
    navigationStore.setUserRedirectedNextStep({
      type: UserRedirectedNextStepTypes.PAYMENT,
      paymentType:
        appStateData.paymentType === PaymentType.INVOICE ? PaymentType.INVOICE : PaymentType.DIRECT,
    });
  }
};

export const mergeGuestDraftsIfEligible = async (
  appStateData: AppState | undefined,
  authenticatedUser: AuthorizedUser | null,
) => {
  const targetPath = appStateData?.targetPath;
  const redirectToBilling =
    (targetPath?.includes('/billing') ?? false) ||
    appStateData?.type === UserRedirectedNextStepTypes.PAYMENT;

  const storageHelper = new StorageHelper('insertion-funnel');
  const guestToken = storageHelper.getLocalStorageItem('guestToken');
  let mergedDrafts: MergeGuestDraftsResponse | undefined;
  if (authenticatedUser && redirectToBilling && guestToken) {
    mergedDrafts = await mergeGuestDrafts(authenticatedUser.userId, guestToken);
  }
  if (mergedDrafts && mergedDrafts.mergedCount > 0) {
    storageHelper.removeLocalStorageItem('guestToken');
  }
};

export const isLegacyIs24GuestExemptFromStorage = async (): Promise<boolean> => {
  const storageHelper = new StorageHelper('insertion-funnel');
  const guestToken = storageHelper.getLocalStorageItem('guestToken');
  if (!guestToken) {
    return false;
  }

  const parsedToken = parseJwt(guestToken);
  const ownerId = parsedToken?.payload?.ownerId;
  if (!ownerId) {
    return false;
  }

  return isIs24LegacyGuestWhitelisted(ownerId);
};

export const checkFeatureFlags = (appStateData: AppState | undefined) => {
  if (appStateData?.feature && Array.isArray(appStateData?.feature)) {
    return appStateData?.feature.join(',');
  }
  return undefined;
};

/**
 * Filters out feature flags that are enabled via split treatment to avoid duplicates
 * @param featureFlagsStore - The feature flags store instance
 * @param flags - Comma-separated string of feature flags
 * @param flagsToFilter - Array of FeatureFlagName to filter out when their split treatments are active
 * @returns Filtered comma-separated string of feature flags
 */
export const getFilteredFeatureFlags = (
  featureFlagsStore: ReturnType<typeof useFeatureFlagsStore>,
  flags: string,
  flagsToFilter: Array<{ flagName: FeatureFlagName; flagValue: string }>,
): string => {
  if (!flags) return flags;

  const featureFlagsArray = flags.split(',');

  const filteredArray = featureFlagsArray.filter((flag) => {
    const trimmedFlag = flag.trim();

    // Check if this flag should be filtered out
    return !flagsToFilter.some(({ flagName, flagValue }) => {
      return trimmedFlag === flagValue && featureFlagsStore.isTreatmentOn(flagName);
    });
  });

  return filteredArray.join(',');
};
